// FILE: src/pages/OwnerPayoutAccount.jsx
import React from "react";
import {
  fetchOwnerPayoutAccount,
  saveOwnerPayoutAccount,
  submitOwnerPayoutAccount,
} from "../api/ownerPortalApi";

/* --------------------- helpers --------------------- */
function safeStr(x) {
  return x == null ? "" : String(x);
}
function up(x) {
  return safeStr(x).trim().toUpperCase();
}
function normIfsc(x) {
  return safeStr(x).trim().toUpperCase().replace(/\s+/g, "");
}
function normUpi(x) {
  return safeStr(x).trim().toLowerCase().replace(/\s+/g, "");
}
function normAcctDigits(x) {
  return safeStr(x).trim().replace(/\s+/g, "");
}

function pickErrMessage(e) {
  if (!e) return "Request failed. Please try again.";
  if (typeof e === "string") return e;
  if (e?.body?.message) return String(e.body.message);
  if (e?.response?.data?.message) return String(e.response.data.message);
  if (e?.data?.message) return String(e.data.message);
  if (e?.message) return String(e.message);
  return "Request failed. Please try again.";
}

/* --------------------- status UI --------------------- */
function displayStatus(code) {
  const s = up(code);
  if (!s || s === "DRAFT") return "SETUP REQUIRED";
  if (s === "SUBMITTED") return "SUBMITTED";
  if (s === "VERIFIED") return "VERIFIED";
  if (s === "ACTION_REQUIRED") return "ACTION REQUIRED";
  return s;
}

function statusTone(code) {
  const s = up(code);
  if (s === "VERIFIED") return "good";
  if (s === "ACTION_REQUIRED") return "bad";
  if (s === "SUBMITTED") return "warn";
  return "neutral";
}

function StatusBadge({ status }) {
  const tone = statusTone(status);
  const cls =
    tone === "good"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : tone === "bad"
      ? "border-rose-200 bg-rose-50 text-rose-700"
      : tone === "warn"
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : "border-slate-200 bg-white text-slate-700";
  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${cls}`}>
      {displayStatus(status)}
    </span>
  );
}

/* --------------------- form UI --------------------- */
function Field({ label, hint, error, children }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <label className="text-sm font-semibold text-slate-800">{label}</label>
        {hint ? <span className="text-xs text-slate-500">{hint}</span> : null}
      </div>
      <div className="mt-1">{children}</div>
      {error ? <div className="mt-1 text-xs text-rose-700">{error}</div> : null}
    </div>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900
                  placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-black/10 ${className}`}
    />
  );
}

function ReadonlyRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-slate-100 last:border-b-0">
      <div className="text-sm font-semibold text-slate-700">{label}</div>
      <div className="text-sm text-slate-900 truncate max-w-[60%] text-right">{safeStr(value) || "—"}</div>
    </div>
  );
}

/* --------------------- validation --------------------- */
const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const UPI_RE = /^[a-z0-9._-]{2,}@[a-z0-9.-]{2,}$/;
const ACCT_RE = /^[0-9]{6,20}$/;

function validateStrict(form) {
  const method = up(form.method || "UPI");
  const errors = {};

  if (method === "UPI") {
    const u = normUpi(form.upiId);
    if (!u) errors.upiId = "UPI ID is required.";
    else if (!UPI_RE.test(u)) errors.upiId = "Invalid UPI. Example: ashutosh@okicici";
    return errors;
  }

  const name = safeStr(form.accountHolderName).trim();
  const bank = safeStr(form.bankName).trim();
  const ifsc = normIfsc(form.ifsc);
  const acct = normAcctDigits(form.accountNumber);

  if (!name) errors.accountHolderName = "Account holder name is required.";
  if (!bank) errors.bankName = "Bank name is required.";

  if (!ifsc) errors.ifsc = "IFSC is required.";
  else if (!IFSC_RE.test(ifsc)) errors.ifsc = "Invalid IFSC. Example: HDFC0001234";

  if (!acct) {
    if (!safeStr(form.accountNumberMasked)) errors.accountNumber = "Account number is required.";
  } else if (!ACCT_RE.test(acct)) {
    errors.accountNumber = "Account number must be 6–20 digits.";
  }

  return errors;
}

function firstError(errors) {
  const keys = Object.keys(errors || {});
  return keys.length ? errors[keys[0]] : "";
}

/* --------------------- component --------------------- */
export default function OwnerPayoutAccount() {
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const [error, setError] = React.useState("");
  const [toast, setToast] = React.useState("");

  const [account, setAccount] = React.useState(null);
  const [editing, setEditing] = React.useState(false);
  const [fieldErrors, setFieldErrors] = React.useState({});

  const [form, setForm] = React.useState({
    method: "UPI",
    accountHolderName: "",
    bankName: "",
    ifsc: "",
    accountNumber: "", // never hydrated from server
    accountNumberMasked: "",
    upiId: "",
  });

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    setToast("");
    try {
      const res = await fetchOwnerPayoutAccount();
      const a = res?.account || res || null;
      setAccount(a);

      const method = a?.method || "UPI";
      const status = up(a?.status || "DRAFT");

      setForm({
        method,
        accountHolderName: a?.accountHolderName || "",
        bankName: a?.bankName || "",
        ifsc: a?.ifsc || "",
        accountNumber: "",
        accountNumberMasked: a?.accountNumberMasked || "",
        upiId: a?.upiId || "",
      });

      const hasAnyData =
        !!safeStr(a?.upiId) ||
        !!safeStr(a?.ifsc) ||
        !!safeStr(a?.accountHolderName) ||
        !!safeStr(a?.bankName) ||
        !!safeStr(a?.accountNumberMasked);

      // ✅ Clean rule:
      // - ACTION_REQUIRED: open edit
      // - DRAFT with no data: open edit (onboarding)
      // - otherwise: read-only summary
      setEditing(status === "ACTION_REQUIRED" || (status === "DRAFT" && !hasAnyData));
      setFieldErrors({});
    } catch (e) {
      console.error(e);
      setError("Failed to load payout details. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const status = up(account?.status || "DRAFT");
  const method = up(form.method || "UPI");

  const hasAnyData =
    !!safeStr(form.upiId) ||
    !!safeStr(form.ifsc) ||
    !!safeStr(form.accountHolderName) ||
    !!safeStr(form.bankName) ||
    !!safeStr(form.accountNumberMasked);

  const isLocked = !editing && (status === "SUBMITTED" || status === "VERIFIED");

  function switchMethod(next) {
    const m = up(next);
    setToast("");
    setError("");
    setFieldErrors({});
    setForm((f) => {
      if (m === "UPI") {
        return {
          ...f,
          method: "UPI",
          accountHolderName: "",
          bankName: "",
          ifsc: "",
          accountNumber: "",
          accountNumberMasked: "",
        };
      }
      return { ...f, method: "BANK", upiId: "" };
    });
  }

  async function onSaveDraft() {
    setToast("");
    setError("");
    setFieldErrors({});

    // Soft validation: only block if clearly invalid formats
    const blocking = {};
    if (method === "UPI") {
      const u = normUpi(form.upiId);
      if (u && !UPI_RE.test(u)) blocking.upiId = "Invalid UPI format.";
    } else {
      const ifsc = normIfsc(form.ifsc);
      if (ifsc && !IFSC_RE.test(ifsc)) blocking.ifsc = "Invalid IFSC format.";
      const acct = normAcctDigits(form.accountNumber);
      if (acct && !ACCT_RE.test(acct)) blocking.accountNumber = "Account number must be 6–20 digits.";
    }

    if (Object.keys(blocking).length) {
      setFieldErrors(blocking);
      setError(firstError(blocking));
      return;
    }

    setSaving(true);
    try {
      const payload =
        method === "UPI"
          ? { method: "UPI", upiId: normUpi(form.upiId) }
          : {
              method: "BANK",
              accountHolderName: safeStr(form.accountHolderName).trim(),
              bankName: safeStr(form.bankName).trim(),
              ifsc: normIfsc(form.ifsc),
              ...(normAcctDigits(form.accountNumber) ? { accountNumber: normAcctDigits(form.accountNumber) } : {}),
            };

      const res = await saveOwnerPayoutAccount(payload);
      const a = res?.account || res;
      setAccount(a);

      setForm((f) => ({
        ...f,
        accountNumber: "",
        accountNumberMasked: a?.accountNumberMasked || f.accountNumberMasked,
      }));

      setToast("Saved.");
    } catch (e) {
      console.error(e);
      setError(pickErrMessage(e));
    } finally {
      setSaving(false);
    }
  }

  async function onSubmit() {
    setToast("");
    setError("");

    const errs = validateStrict(form);
    setFieldErrors(errs);

    const msg = firstError(errs);
    if (msg) {
      setError(msg);
      return;
    }

    setSubmitting(true);
    try {
      const payload =
        method === "UPI"
          ? { method: "UPI", upiId: normUpi(form.upiId) }
          : {
              method: "BANK",
              accountHolderName: safeStr(form.accountHolderName).trim(),
              bankName: safeStr(form.bankName).trim(),
              ifsc: normIfsc(form.ifsc),
              accountNumber: normAcctDigits(form.accountNumber),
            };

      const res = await submitOwnerPayoutAccount(payload);
      const a = res?.account || res;
      setAccount(a);

      setForm((f) => ({
        ...f,
        accountNumber: "",
        accountNumberMasked: a?.accountNumberMasked || f.accountNumberMasked,
      }));

      setFieldErrors({});
      setEditing(false);
      setToast("Submitted for verification.");
    } catch (e) {
      console.error(e);
      setError(pickErrMessage(e));
    } finally {
      setSubmitting(false);
    }
  }

  const headerHint =
    status === "VERIFIED"
      ? "Your payout destination is verified."
      : status === "SUBMITTED"
      ? "Your details are under review."
      : status === "ACTION_REQUIRED"
      ? "Please fix the issue and submit again."
      : "Add payout details to receive earnings.";

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-8">
      {/* Top header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-900">Payout account</h1>
            <button
              type="button"
              onClick={load}
              className="text-xs font-semibold text-slate-500 hover:text-slate-800"
              title="Reload"
            >
              Reload
            </button>
          </div>
          <p className="mt-1 text-sm text-slate-600">{headerHint}</p>
        </div>

        <div className="shrink-0 flex items-center gap-2">
          <StatusBadge status={account?.status} />
          {!editing && (
            <button
              type="button"
              onClick={() => {
                setEditing(true);
                setToast("");
                setError("");
              }}
              className="h-9 rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 hover:bg-slate-50"
            >
              {hasAnyData ? (status === "DRAFT" ? "Continue setup" : "Edit") : "Set up"}
            </button>
          )}
        </div>
      </div>

      {/* Action required note */}
      {account?.issueNote ? (
        <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4">
          <div className="text-sm font-semibold text-rose-800">Action required</div>
          <div className="mt-1 text-sm text-rose-700 whitespace-pre-wrap">{account.issueNote}</div>
        </div>
      ) : null}

      {editing && status === "VERIFIED" ? (
        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <div className="text-sm font-semibold text-amber-900">Editing will require re-verification</div>
          <div className="mt-1 text-sm text-amber-800">
            If you change payout details, please submit again for verification.
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      {toast ? (
        <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
          {toast}
        </div>
      ) : null}

      {/* Main card */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="p-5 sm:p-6">
          {loading ? (
            <div className="text-sm text-slate-600">Loading…</div>
          ) : (
            <>
              {/* READ-ONLY SUMMARY (default) */}
              {!editing ? (
                <div>
                  {!hasAnyData ? (
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="text-sm font-semibold text-slate-900">No payout details added</div>
                      <div className="mt-1 text-sm text-slate-600">
                        Add UPI or Bank details to receive payouts.
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-slate-200 bg-white">
                      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 rounded-t-2xl">
                        <div className="text-xs font-semibold text-slate-500">Saved destination</div>
                        <div className="mt-0.5 text-sm font-semibold text-slate-900">
                          Method: {method === "BANK" ? "Bank" : "UPI"}
                        </div>
                      </div>

                      <div className="px-4">
                        {method === "UPI" ? (
                          <ReadonlyRow label="UPI ID" value={form.upiId} />
                        ) : (
                          <>
                            <ReadonlyRow label="Account holder" value={form.accountHolderName} />
                            <ReadonlyRow label="Bank" value={form.bankName} />
                            <ReadonlyRow label="IFSC" value={form.ifsc} />
                            <ReadonlyRow label="Account number" value={form.accountNumberMasked || "—"} />
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="mt-4 text-xs text-slate-500">
                    We never display your full bank account number for security.
                  </div>
                </div>
              ) : (
                /* EDIT FORM */
                <div>
                  {/* Method switch only in edit mode */}
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      disabled={isLocked}
                      onClick={() => switchMethod("UPI")}
                      className={`h-11 rounded-xl border px-4 text-sm font-semibold transition disabled:opacity-50 ${
                        method === "UPI"
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                      }`}
                    >
                      UPI
                    </button>
                    <button
                      type="button"
                      disabled={isLocked}
                      onClick={() => switchMethod("BANK")}
                      className={`h-11 rounded-xl border px-4 text-sm font-semibold transition disabled:opacity-50 ${
                        method === "BANK"
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                      }`}
                    >
                      Bank
                    </button>
                  </div>

                  <div className="mt-6 grid grid-cols-1 gap-4">
                    {method === "UPI" ? (
                      <Field label="UPI ID" hint="Example: ashutosh@okicici" error={fieldErrors.upiId}>
                        <Input
                          value={form.upiId}
                          onChange={(e) => {
                            setForm((f) => ({ ...f, upiId: e.target.value }));
                            setFieldErrors((fe) => ({ ...fe, upiId: undefined }));
                          }}
                          placeholder="yourname@bank"
                          autoComplete="off"
                          inputMode="email"
                        />
                      </Field>
                    ) : (
                      <>
                        <Field label="Account holder name" error={fieldErrors.accountHolderName}>
                          <Input
                            value={form.accountHolderName}
                            onChange={(e) => {
                              setForm((f) => ({ ...f, accountHolderName: e.target.value }));
                              setFieldErrors((fe) => ({ ...fe, accountHolderName: undefined }));
                            }}
                            placeholder="Full name as per bank"
                            autoComplete="off"
                          />
                        </Field>

                        <Field label="Bank name" error={fieldErrors.bankName}>
                          <Input
                            value={form.bankName}
                            onChange={(e) => {
                              setForm((f) => ({ ...f, bankName: e.target.value }));
                              setFieldErrors((fe) => ({ ...fe, bankName: undefined }));
                            }}
                            placeholder="Example: State Bank of India"
                            autoComplete="off"
                          />
                        </Field>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <Field
                            label="IFSC"
                            hint="Example: HDFC0001234"
                            error={fieldErrors.ifsc}
                          >
                            <Input
                              value={form.ifsc}
                              onChange={(e) => {
                                setForm((f) => ({ ...f, ifsc: normIfsc(e.target.value) }));
                                setFieldErrors((fe) => ({ ...fe, ifsc: undefined }));
                              }}
                              placeholder="HDFC0001234"
                              autoComplete="off"
                            />
                          </Field>

                          <Field
                            label="Account number"
                            hint={form.accountNumberMasked ? `Saved: ${form.accountNumberMasked} (enter again only to update)` : "6–20 digits"}
                            error={fieldErrors.accountNumber}
                          >
                            <Input
                              value={form.accountNumber}
                              onChange={(e) => {
                                const cleaned = e.target.value.replace(/[^\d\s]/g, "");
                                setForm((f) => ({ ...f, accountNumber: cleaned }));
                                setFieldErrors((fe) => ({ ...fe, accountNumber: undefined }));
                              }}
                              placeholder={form.accountNumberMasked ? "Enter new account number" : "123456789012"}
                              autoComplete="off"
                              inputMode="numeric"
                            />
                          </Field>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Clean actions */}
                  <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                    <button
                      type="button"
                      disabled={saving || submitting}
                      onClick={() => {
                        setEditing(false);
                        setError("");
                        setToast("");
                        setFieldErrors({});
                        load(); // discard edits -> revert
                      }}
                      className="h-11 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50"
                    >
                      Discard
                    </button>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        type="button"
                        disabled={saving || submitting}
                        onClick={onSaveDraft}
                        className="h-11 rounded-xl border border-slate-200 bg-white px-5 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-50"
                      >
                        {saving ? "Saving…" : "Save draft"}
                      </button>

                      <button
                        type="button"
                        disabled={saving || submitting}
                        onClick={onSubmit}
                        className="h-11 rounded-xl border border-slate-900 bg-slate-900 px-5 text-sm font-semibold text-white hover:bg-black disabled:opacity-50"
                      >
                        {submitting ? "Submitting…" : "Submit for verification"}
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 text-xs text-slate-500">
                    Only one payout method is active at a time. Switching method replaces the destination.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
